import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';

export interface MediaColumn {
  key: string;
  label: string;
  width: string;
  align?: 'left' | 'center' | 'right';
}

export interface MediaTableRow {
  id: string;
  cells: Record<string, ReactNode>;
  onClick?: () => void;
}

interface MediaTableProps {
  columns: MediaColumn[];
  rows: MediaTableRow[];
}

function justifyContentFor(align: MediaColumn['align']): string {
  if (align === 'center') return 'center';
  if (align === 'right') return 'flex-end';
  return 'flex-start';
}

export function MediaTable({ columns, rows }: MediaTableProps): ReactNode {
  const gridTemplateColumns = columns.map((column) => column.width).join(' ');
  return (
    <Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          width: 'fit-content',
          minWidth: '100%',
          gap: 3,
          px: 4,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {columns.map((column) => (
          <Box
            key={column.key}
            sx={{ display: 'flex', justifyContent: justifyContentFor(column.align), minWidth: 0 }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                color: 'text.secondary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {column.label}
            </Typography>
          </Box>
        ))}
      </Box>
      {rows.map((row, rowIndex) => (
        <Box
          key={row.id}
          onClick={row.onClick}
          sx={{
            display: 'grid',
            gridTemplateColumns,
            width: 'fit-content',
            minWidth: '100%',
            gap: 3,
            alignItems: 'center',
            py: 3,
            px: 4,
            borderBottom: rowIndex < rows.length - 1 ? '1px solid' : 'none',
            borderColor: 'divider',
            cursor: row.onClick === undefined ? undefined : 'pointer',
            '&:hover': row.onClick === undefined ? undefined : { bgcolor: 'background.page' },
          }}
        >
          {columns.map((column) => (
            <Box
              key={column.key}
              sx={{
                display: 'flex',
                justifyContent: justifyContentFor(column.align),
                color: 'text.primary',
                minWidth: 0,
              }}
            >
              {row.cells[column.key]}
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );
}
